import React from "react";
import type { ILineStatusItem, LineStatusValue } from "@/lib/backend/manufacturing2.generated.types";
import Tooltip from "../ui/Tooltip";
import LineStatusDetails from "./LineStatusDetails";
import StatusDot from "./StatusDot";
import { getLineStatusDisplay } from "./lineStatusDisplay";

type Props = {
    status: ILineStatusItem | Error;
    cx?: number;
    cy?: number;
    r?: number;
    className?: string;
    /** When true, wraps the circle in an SVG for use outside an existing SVG context. */
    standaloneSvg?: boolean;
    /** When false, renders the status dot without a hover tooltip. Defaults to true. */
    showTooltip?: boolean;
};

const DEFAULT_R = 5;

/** Semantic CSS custom properties from vars.scss (--color-*). */
const LINE_STATUS_FILL: Record<LineStatusValue, string> = {
    stopped: "var(--color-error)",
    idle: "var(--color-warning)",
    working: "var(--color-success)",
    in_preparation: "var(--color-info)",
    unknown: "var(--color-neutral)",
};

const STALE_OPACITY = 0.5;

function getStatusFill(status: LineStatusValue): string {
    return LINE_STATUS_FILL[status] ?? LINE_STATUS_FILL.unknown;
}

const LineStatus = (props: Props) => {
    const { status, cx, cy, r = DEFAULT_R, standaloneSvg = true, showTooltip = true, className } = props;

    let fill = "";
    let opacity = 1;
    let tooltipKey = "";
    let tooltipContentKey = "";
    let tooltipContent: React.ReactNode = "";

    if (status instanceof Error) {
        fill = "black";
        tooltipKey = `tooltip-error`;
        tooltipContentKey = status.message;
        tooltipContent = status.message;
    } else {
        const display = getLineStatusDisplay(status);
        fill = getStatusFill(display.status);
        opacity = display.stale ? STALE_OPACITY : 1;
        tooltipContent = <LineStatusDetails status={status} />;
        tooltipKey = status.lineUuid;
        tooltipContentKey = JSON.stringify(status);
    }

    const statusDot = (
        <StatusDot
            cx={cx}
            cy={cy}
            r={r}
            fill={fill}
            opacity={opacity}
            standaloneSvg={standaloneSvg}
            className={className}
        />
    );

    if (showTooltip) {
        return (
            <Tooltip key={tooltipKey} content_key={tooltipContentKey} content={tooltipContent}>
                {statusDot}
            </Tooltip>
        );
    }

    return statusDot;
};

export default LineStatus;
